import { useEffect, useReducer, useState } from 'react'

import moment from 'moment'
import { connect } from 'react-redux'

import AdvancedActivityTable from 'app/agency/advance_activity/activity_table'
import { Box, LoadingSection } from 'app/components/design-system-components'
import { fontSizes } from 'app/components/design-system-components/styles/variables'
import { translateErrorCodeToMessage } from 'app/constants/error_messages'
import * as actFilters from 'app/dashboard/team_activity/ActivityFilters'
import { activityPeriods } from 'app/dashboard/team_activity/ActivityFilters/DateRangeFilter/constants'
import {
  fetchAdvanceTeamActivities,
  fetchPropertiesActivitiesReport,
} from 'app/dashboard/team_activity/team_activity_action'
import { getTeamManagers } from 'app/services/http/teams/managers'
import { getTeamSettings } from 'app/services/http/teams/settings'
import * as snugNotifier from 'app/services/snugNotifier'
import { parseDataUtil } from 'app/sm/helpers'
import { getAgencyListPods } from 'app/sm/team_viewing_preference/access'
import * as dateTimeHelpers from 'app/utils/datetime/helpers'
import { useStateWithLoading } from 'app/utils/hooks/useStateWithLoading'
import { loadingStatesIds } from 'app/utils/loading-states'
import {
  filterReducerFactory,
  filtersActions,
} from 'app/utils/reducers/filtersReducer'
import * as Features from 'config/features'

const Filters = ({
  filtersState,
  filtersDispatch,
  currentTeam,
  managers,
  periodSetter,
  pods,
  isPodsDisabled,
}) => (
  <Box ml={2}>
    <actFilters.ActivitiesFilters
      teamId={currentTeam.guid}
      initialPeriod={activityPeriods.currentMonth}
      managers={managers}
      pods={pods}
      includeManagersFilter
      filters={filtersState.filters}
      updateFilter={(filterId, filterValues) =>
        filtersDispatch(filtersActions.updateFilter(filterId, filterValues))
      }
      onPeriodChange={periodSetter}
      includePodsFilter={Features.isPodsFilterEnabledForTeam(currentTeam.slug)}
      isPodsDisabled={isPodsDisabled}
    />
  </Box>
)

export const AdvancedActivityReporting = ({
  currentTeam,
  teamsLoaded,
  fetchAdvanceTeamActivities,
  fetchPropertiesActivitiesReport,
}) => {
  const {
    state: filtersLoadedData,
    setState: setFiltersLoadedData,
    loadingStates: filtersLoadingStates,
    loadingStatesHelpers: setFiltersLoadingStatesHelpers,
  } = useStateWithLoading({})

  const {
    state: advancedActData,
    setState: setAdvancedActData,
    loadingStates: advancedActLoadingStates,
    loadingStatesHelpers: setAdvancedActLoadingStatesHelpers,
  } = useStateWithLoading([])

  const {
    state: propertiesActData,
    setState: setPropertiesActData,
    loadingStates: propertiesActLoadingStates,
    loadingStatesHelpers: setPropertiesActLoadingStatesHelpers,
  } = useStateWithLoading([])

  const [filtersState, filtersDispatch] = useReducer(
    filterReducerFactory(actFilters.filtersInitialState),
    actFilters.filtersInitialState,
  )

  const [filterSelectedPeriod, setFilterSelectedPeriod] = useState()

  const [groupBundleEnabled, setGroupBundleEnabled] = useState(false)

  useEffect(() => {
    if (currentTeam && currentTeam.guid) {
      loadFiltersBasicData()
    }
  }, [currentTeam])

  useEffect(() => {
    if (filtersLoadingStates.state !== loadingStatesIds.LOADED) return
    loadActivities()
  }, [filtersState])

  const loadFiltersBasicData = () => {
    // Wait for teams to load and ensure currentTeam exists
    if (!teamsLoaded || !currentTeam || !currentTeam.guid) {
      return Promise.resolve([{}, {}, {}])
    }
    setFiltersLoadingStatesHelpers.startLoading()
    return Promise.all([
      getTeamManagers(currentTeam.guid),
      getAgencyListPods(currentTeam.guid),
      getTeamSettings(currentTeam.guid, {
        keys: ['bundle'],
      }),
    ])
      .then(([{ managerList }, podsList, { settingInfo }]) => {
        setFiltersLoadedData({ managers: managerList, pods: podsList })
        const { bundle } = settingInfo
        const bundles = parseDataUtil.convertObjValueFromStringToBoolean(bundle)

        setGroupBundleEnabled(bundles.group_bundle)
        setFiltersLoadingStatesHelpers.markDoneSuccessfully()
      })
      .catch((err) => setFiltersLoadingStatesHelpers.setError(err))
  }

  const loadActivities = () => {
    setAdvancedActLoadingStatesHelpers.startLoading()
    const {
      range,
      managers = [],
      dateFilterBy,
      teams = [],
      pods = [],
    } = actFilters.getFilters(filtersState)

    const formattedRange = dateTimeHelpers.formatFilterRangeToUTC(range)
    const formattedPriorMonthRange = dateTimeHelpers.formatFilterRangeToUTC({
      startDate: dateTimeHelpers.getStartOfPriorMonth(),
      endDate: dateTimeHelpers.getEndOfPriorMonth(),
    })
    const formattedPriorYearMonthRange = dateTimeHelpers.formatFilterRangeToUTC(
      {
        startDate: dateTimeHelpers.getStartOfPriorYearCurrentMonth(),
        endDate: dateTimeHelpers.getEndOfPriorYearCurrentMonth(),
      },
    )
    const teamsIds = teams?.map((team) => team?.agencyInfo?.agencyGUID) || []

    fetchAdvanceTeamActivities(currentTeam.guid, {
      ...formattedRange,
      manager: managers.map((manager) => manager.guidID),
      dateFilterBy,
      teams: teamsIds,
      pods: pods?.map((pod) => pod.id) || [],
      startOfPriorMonth: formattedPriorMonthRange.startDate,
      endOfPriorMonth: formattedPriorMonthRange.endDate,
      startOfPriorYearCurrentMonth: formattedPriorYearMonthRange.startDate,
      endOfPriorYearCurrentMonth: formattedPriorYearMonthRange.endDate,
    })
      .then(({ advanceActivities }) => {
        setAdvancedActLoadingStatesHelpers.markDoneSuccessfully()
        setAdvancedActData(advanceActivities)
      })
      .catch((error) => {
        snugNotifier.error(translateErrorCodeToMessage(error))
        setAdvancedActLoadingStatesHelpers.setError(error)
      })
    setPropertiesActLoadingStatesHelpers.startLoading()

    fetchPropertiesActivitiesReport(currentTeam.guid, {
      startTime: formattedRange.startDate,
      endTime: formattedRange.endDate,
      dateFilterBy,
      manager: managers.map((manager) => manager.guidID),
      pods: pods?.map((pod) => pod.id) || [],
      team: teamsIds,
    })
      .then((propertiesActivities) => {
        setPropertiesActLoadingStatesHelpers.markDoneSuccessfully()
        setPropertiesActData(propertiesActivities)
      })
      .catch((error) => {
        snugNotifier.error(translateErrorCodeToMessage(error))
        setPropertiesActLoadingStatesHelpers.setError(error)
      })
  }

  let rangeLabel
  const { range = {} } = actFilters.getFilters(filtersState)
  const formattedRange = dateTimeHelpers.formatFilterRangeToUTC(range)
  if (filterSelectedPeriod?.optionMeta?.id !== activityPeriods.custom) {
    rangeLabel = filterSelectedPeriod?.optionMeta?.displayText
  } else {
    const startDateLabel = range.startDate
      ? moment(range.startDate).format('DD MMM YY')
      : ''
    const endDateLabel = range.endDate
      ? moment(range.endDate).format('DD MMM YY')
      : ''
    const prefix = !!startDateLabel && !endDateLabel ? 'from ' : ''
    rangeLabel = prefix + startDateLabel + ' to ' + endDateLabel
  }

  return (
    <>
      {advancedActData && (
        <>
          <LoadingSection loadingState={filtersLoadingStates}>
            <Filters
              currentTeam={currentTeam}
              filtersState={filtersState}
              managers={filtersLoadedData?.managers}
              periodSetter={setFilterSelectedPeriod}
              filtersDispatch={filtersDispatch}
              pods={filtersLoadedData?.pods}
              isPodsDisabled={!groupBundleEnabled}
            />
          </LoadingSection>
          <LoadingSection
            loadingState={propertiesActLoadingStates}
            actionHandler={loadActivities}
            loaderProps={{ fontSize: fontSizes[13] + 'px' }}
          >
            <AdvancedActivityTable
              advanceActivities={advancedActData}
              propertiesActivitiesReport={propertiesActData}
              propertiesReportLoadingStates={propertiesActLoadingStates}
              customColumnSelector={`${formattedRange.startDate || ''} - ${
                formattedRange.endDate || ''
              }`}
              customColumnLabel={rangeLabel}
              error={propertiesActLoadingStates.error?.message}
              filters={filtersState.filters}
              onFiltersUpdated={(filterId, filterValues) =>
                filtersDispatch(
                  filtersActions.updateFilter(filterId, filterValues),
                )
              }
            />
          </LoadingSection>
        </>
      )}
    </>
  )
}

const mapStateToProps = ({ session }) => ({
  currentTeam: session.currentTeam,
  teamsLoaded: session.teamsLoaded,
})

const mapDispatchToProps = (dispatch) => ({
  fetchAdvanceTeamActivities: (teamGUID, filters) =>
    dispatch(fetchAdvanceTeamActivities(teamGUID, filters)),
  fetchPropertiesActivitiesReport: (teamGUID, options) =>
    dispatch(() => fetchPropertiesActivitiesReport(teamGUID, options)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AdvancedActivityReporting)
